import React, { useMemo } from 'react';
import Select, { components as SelectComponents } from 'react-select';
import { FiX, FiCheck } from 'react-icons/fi';
import classNames from 'classnames';

import styles from './IconChipMultiSelect.module.scss';

export const iconChipMultiSelectTones = {
  productive: 'productive',
  distracting: 'distracting',
};

const TONE_ACCENT = {
  [iconChipMultiSelectTones.productive]: '#22c55e',
  [iconChipMultiSelectTones.distracting]: '#ef4444',
};

const buildSelectStyles = (accentColor) => ({
  container: (base) => ({ ...base, width: '100%' }),
  control: (base, state) => ({
    ...base,
    border: `1.5px solid ${state.isFocused ? accentColor : '#d1d5db'}`,
    borderRadius: '8px',
    boxShadow: state.isFocused ? `0 0 0 3px ${accentColor}22` : 'none',
    minHeight: '38px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': { borderColor: accentColor },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? `${accentColor}18`
      : state.isFocused
        ? '#f3f4f6'
        : 'white',
    color: state.isSelected ? accentColor : '#111827',
    fontWeight: state.isSelected ? 500 : 400,
    fontSize: '13px',
    cursor: 'pointer',
    '&:active': { backgroundColor: `${accentColor}28` },
  }),
  placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9ca3af' }),
  input: (base) => ({ ...base, fontSize: '13px' }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    border: '1px solid #e0e4ea',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

// Hides react-select's own multi-value pills — selected items are instead
// rendered as the icon chips below, so the closed control just shows a
// "N selected" count instead of a cramped row of native pills.
const ValueContainer = ({ children, getValue, selectProps, ...props }) => {
  const selected = getValue();
  const showCount =
    selected.length > 0 && !selectProps.menuIsOpen && !selectProps.inputValue;
  return (
    <SelectComponents.ValueContainer
      getValue={getValue}
      selectProps={selectProps}
      {...props}
    >
      {showCount && (
        <span className={styles.selectedCount}>{selected.length} selected</span>
      )}
      {children}
    </SelectComponents.ValueContainer>
  );
};

/**
 * Multi-select dropdown + removable icon chips underneath — the dropdown's
 * own value pills are hidden (see ValueContainer) since the chip row is the
 * single source of truth for what's selected.
 */
export const IconChipMultiSelect = ({
  options,
  value,
  onChange,
  iconResolver,
  tone = iconChipMultiSelectTones.productive,
  placeholder = 'Select…',
  isLoading = false,
  isDisabled = false,
  emptyMessage = 'Not yet selected.',
}) => {
  const accentColor = TONE_ACCENT[tone];
  const selectStyles = useMemo(
    () => buildSelectStyles(accentColor),
    [accentColor]
  );

  const OptionWithIcon = useMemo(
    () =>
      function OptionWithIcon(props) {
        return (
          <SelectComponents.Option {...props}>
            <div className={styles.optionRow}>
              <span className={styles.optionLabel}>
                {iconResolver?.(props.label)}
                {props.label}
              </span>
              {props.isSelected && <FiCheck size={13} />}
            </div>
          </SelectComponents.Option>
        );
      },
    [iconResolver]
  );

  const selectComponents = useMemo(
    () => ({
      Option: OptionWithIcon,
      MultiValue: () => null,
      ValueContainer,
    }),
    [OptionWithIcon]
  );

  const selectOptions = useMemo(
    () => options.map((name) => ({ value: name, label: name })),
    [options]
  );
  const selectValue = useMemo(
    () => value.map((name) => ({ value: name, label: name })),
    [value]
  );

  const removeItem = (item) => onChange(value.filter((v) => v !== item));

  return (
    <div className={styles.iconChipMultiSelect}>
      <Select
        isMulti
        options={selectOptions}
        value={selectValue}
        onChange={(selected) => onChange((selected ?? []).map((s) => s.value))}
        components={selectComponents}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        backspaceRemovesValue={false}
        isLoading={isLoading}
        isDisabled={isDisabled}
        placeholder={placeholder}
        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        styles={selectStyles}
      />

      <div className={styles.chips}>
        {value.length === 0 ? (
          <span className={styles.chipsEmpty}>{emptyMessage}</span>
        ) : (
          value.map((item) => (
            <span
              key={item}
              className={classNames(styles.chip, styles[`chip_tone_${tone}`])}
            >
              {iconResolver?.(item)}
              {item}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removeItem(item)}
                title={`Remove ${item}`}
              >
                <FiX size={11} />
              </button>
            </span>
          ))
        )}
      </div>
    </div>
  );
};

IconChipMultiSelect.displayName = 'IconChipMultiSelect';
